// noinspection JSUnusedGlobalSymbols

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type Method,
  type InternalAxiosRequestConfig,
} from "axios"

import {config, api, routes} from "@isudio/config";
import {getAuthToken, refreshToken} from "./core.services";
import HttpStatus from "../constants/http.constants";

export interface ApiRouteConfig {
  verb: Method
  name?: string
  auth?: boolean
  endPoint: string
  queryParams?: boolean
  contentType: string
  serializer: "json" | "multipart" | "form-data"
  responseType: "json" | "blob" | "text" | "arraybuffer"
  internal?: boolean
  onlineCheck?: boolean
}

export interface ApiConfig {
  version: string
  url: string
  routes: Record<string, ApiRouteConfig>
}

export interface ApiRequestOptions {
  data?: any
  params?: Record<string, any>
  headers?: Record<string, string>
  timeout?: number
  auth?: boolean
  onUploadProgress?: (progressEvent: any) => void
  onDownloadProgress?: (progressEvent: any) => void
}

export interface ApiServiceConfig {
  baseURL?: string
  timeout?: number
  defaultHeaders?: Record<string, string>
  authTokenProvider?: typeof getAuthToken
  authHeaderName?: string
  interceptors?: {
    request?: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>
    response?: (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>
    error?: (error: any) => Promise<any>
  }
}

export class ApiService {
  private readonly axiosInstance: AxiosInstance;
  private config: ApiServiceConfig;
  private apiConfig?: ApiConfig = (api as any);

  constructor(config: ApiServiceConfig = {}) {
    this.config = {
      timeout: 30000,
      authHeaderName: "Authorization",
      authTokenProvider: getAuthToken,
      ...config,
    }

    this.axiosInstance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        //"Content-Type": "application/json",
        ...this.config.defaultHeaders,
      },
    })

    this.setupInterceptors()
  }

  setApiConfig(apiConfig: ApiConfig): void {
    this.apiConfig = apiConfig
  }

  private setupInterceptors(): void {
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        if (this.config.interceptors?.request) {
          config = await this.config.interceptors.request(config)
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    this.axiosInstance.interceptors.response.use(
      async (response) => {
        if (this.config.interceptors?.response) {
          response = await this.config.interceptors.response(response)
        }
        return response
      },
      async (error) => {
        const originalRequest = error.config;

        // If 401 and we haven’t retried yet
        if (error.response?.status === HttpStatus.UNAUTHORIZED && !originalRequest._retry) {
          if (originalRequest.url?.includes(routes.refreshToken)) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            // Refresh token using CoreServices
            const newAccessToken = await refreshToken();

            if (newAccessToken) {
              // Replace the old token in headers
              originalRequest.headers[this.config.authHeaderName!] = `Bearer ${newAccessToken}`;
              // Retry the original request
              return this.axiosInstance(originalRequest);
            }
          } catch (refreshErr) {
            console.error("Token refresh failed:", refreshErr);
          }
        }

        if (this.config.interceptors?.error) {
          return await this.config.interceptors.error(error);
        }

        return Promise.reject(error);
      }
    )
  }

  private async addAuthHeader(config: AxiosRequestConfig, requireAuth: boolean, endPoint?: string): Promise<void> {
    if (requireAuth && this.config.authTokenProvider) {
      const token = await this.config.authTokenProvider(endPoint)
      if (token) {
        if (!config.headers) {
          config.headers = {}
        }
        config.headers[this.config.authHeaderName!] = token.startsWith("Bearer ") ? token : `Bearer ${token}`
      }
    }
  }

  private serializeData(data: any, serializer: string, _contentType: string): any {
    if (!data) return data

    switch (serializer) {
      case "multipart":
        const formData = new FormData()
        Object.keys(data).forEach((key) => {
          if (data[key] instanceof File || data[key] instanceof Blob) {
            formData.append(key, data[key])
          } else if (Array.isArray(data[key])) {
            data[key].forEach((item: any, index: number) => {
              if (item instanceof File || item instanceof Blob) {
                formData.append(`${key}[${index}]`, item)
              } else {
                formData.append(`${key}[${index}]`, JSON.stringify(item))
              }
            })
          } else if (typeof data[key] === "object") {
            formData.append(key, JSON.stringify(data[key]))
          } else {
            formData.append(key, data[key])
          }
        })
        return formData

      case "form-data":
        const urlSearchParams = new URLSearchParams()
        Object.keys(data).forEach((key) => {
          if (typeof data[key] === "object") {
            urlSearchParams.append(key, JSON.stringify(data[key]))
          } else {
            urlSearchParams.append(key, data[key])
          }
        })
        return urlSearchParams

      case "json":
      default:
        return data
    }
  }

  private async buildRequestConfig(
    method: Method,
    url: string,
    routeConfig?: ApiRouteConfig,
    options: ApiRequestOptions = {},
  ): Promise<AxiosRequestConfig> {
    let finalUrl: string = url;

    // 1. If url matches a route name in config → build from api.url + api.version
    if (this.apiConfig && this.apiConfig.routes[url]) {
      routeConfig = this.apiConfig.routes[url];
      const baseUrl = routeConfig?.internal !== false ? this.apiConfig.url : "";
      finalUrl = `${baseUrl}${this.apiConfig.version}/${routeConfig?.endPoint}`;

      // Replace path params (:id etc.)
      const params = options?.params || options?.data?.params;
      if (params) {
        finalUrl = finalUrl.replace(/:([a-zA-Z0-9_]+)/g, (_, key) => {
          if (params![key] !== undefined) {
            const value = encodeURIComponent(params![key]);
            delete params![key];
            return value;
          }
          return _;
        });
      }
    }

    const config: AxiosRequestConfig = {
      method,
      url: finalUrl,
      timeout: options.timeout || this.config.timeout,
      headers: {
        ...options.headers,
      }
    }

    if (routeConfig) {
      config.headers!["Content-Type"] = routeConfig.contentType
      config.responseType = routeConfig.responseType as any

      await this.addAuthHeader(config, routeConfig.auth || options.auth || false, routeConfig.endPoint)

      if (options.data) {
        config.data = this.serializeData(options.data, routeConfig.serializer, routeConfig.contentType)
      }

      if (routeConfig.queryParams && options.params) {
        config.params = options.params
      }

    } else {
      await this.addAuthHeader(config, options.auth || false)

      if (options.data) {
        config.data = options.data
      }

      if (options.params) {
        config.params = options.params
      }
    }

    if (options.onUploadProgress) {
      config.onUploadProgress = options.onUploadProgress
    }

    if (options.onDownloadProgress) {
      config.onDownloadProgress = options.onDownloadProgress
    }

    return config
  }

  async request<T = any>(method: Method, url: string, options: ApiRequestOptions = {}): Promise<AxiosResponse<T>> {
    const config = await this.buildRequestConfig(
      method,
      url,
      undefined,
      options
    );

    return this.axiosInstance.request<T>(config)
  }

  async requestByRoute<T = any>(
    routeName: string,
    options: ApiRequestOptions = {},
    customUrl?: string,
  ): Promise<AxiosResponse<T>> {
    if (!this.apiConfig) {
      throw new Error("API configuration not set. Call setApiConfig() first.")
    }

    const routeConfig = this.apiConfig.routes[routeName]
    if (!routeConfig) {
      throw new Error(`Route '${routeName}' not found in API configuration.`)
    }

    let url: string
    if (customUrl) {
      url = customUrl
    } else {
      const baseUrl = routeConfig.internal !== false ? this.apiConfig.url : ""
      url = `${baseUrl}${this.apiConfig.version}/${routeConfig.endPoint}`
    }

    const config = await this.buildRequestConfig(routeConfig.verb, url, routeConfig, options)
    return this.axiosInstance.request<T>(config)
  }

  async get<T = any>(url: string, options: ApiRequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>("GET", url, options)
  }

  async post<T = any>(url: string, data?: any, options: ApiRequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>("POST", url, { ...options, data })
  }

  async put<T = any>(url: string, data?: any, options: ApiRequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>("PUT", url, { ...options, data })
  }

  async patch<T = any>(url: string, data?: any, options: ApiRequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>("PATCH", url, { ...options, data })
  }

  async delete<T = any>(url: string, options: ApiRequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>("DELETE", url, options)
  }

  async getByRoute<T = any>(
    routeName: string,
    options: ApiRequestOptions = {},
    customUrl?: string,
  ): Promise<AxiosResponse<T>> {
    return this.requestByRoute<T>(routeName, options, customUrl)
  }

  async postByRoute<T = any>(
    routeName: string,
    data?: any,
    options: ApiRequestOptions = {},
    customUrl?: string,
  ): Promise<AxiosResponse<T>> {
    return this.requestByRoute<T>(routeName, { ...options, data }, customUrl)
  }

  async putByRoute<T = any>(
    routeName: string,
    data?: any,
    options: ApiRequestOptions = {},
    customUrl?: string,
  ): Promise<AxiosResponse<T>> {
    return this.requestByRoute<T>(routeName, { ...options, data }, customUrl)
  }

  async patchByRoute<T = any>(
    routeName: string,
    data?: any,
    options: ApiRequestOptions = {},
    customUrl?: string,
  ): Promise<AxiosResponse<T>> {
    return this.requestByRoute<T>(routeName, { ...options, data }, customUrl)
  }

  async deleteByRoute<T = any>(
    routeName: string,
    options: ApiRequestOptions = {},
    customUrl?: string,
  ): Promise<AxiosResponse<T>> {
    return this.requestByRoute<T>(routeName, options, customUrl)
  }

  async downloadFile(
    url: string,
    options: ApiRequestOptions & { filename?: string } = {},
  ): Promise<{ data: Blob; filename?: string }> {
    const response = await this.request<Blob>("GET", url, {
      ...options,
      headers: {
        ...options.headers,
        Accept: "application/octet-stream",
      },
    })

    const contentDisposition = response.headers["content-disposition"]
    let filename = options.filename

    if (!filename && contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch) {
        filename = filenameMatch[1].replace(/['"]/g, "")
      }
    }

    return {
      data: response.data,
      filename,
    }
  }

  async uploadFile(
    url: string,
    file: File | Blob,
    fieldName = "file",
    additionalData: Record<string, any> = {},
    options: ApiRequestOptions = {},
  ): Promise<AxiosResponse> {
    const formData = new FormData()
    formData.append(fieldName, file)

    Object.keys(additionalData).forEach((key) => {
      formData.append(key, additionalData[key])
    })

    return this.post(url, formData, {
      ...options,
      headers: {
        ...options.headers,
      },
    })
  }
}

export const $api: ApiService = new ApiService()
if (typeof window !== 'undefined' && config.app.debug) (window as any).$api = $api;

export const createApiService = (config: ApiServiceConfig): ApiService => {
  return new ApiService(config)
}
